// Singleton cache for in-flight requests
// This is a simple in-memory cache for ongoing requests, it will be instantiated
// when the module is loaded. This is not a singleton in the strictest sense, but
// rather a module-level cache.
const inFlightRequests = new Map();

/**
 * Caches ongoing requests by key. If a request is already in progress for a given key,
 * returns the same promise. Once completed, the key is removed.
 * @param {string} key - The unique key for the request.
 * @param {function} requestFn - The function that performs the request.
 * @returns {Promise} - The promise of the request.
 * @throws {Error} - If the key is not a string or if the requestFn is not a function.
 * @example
 * const data = await cachedInflightRequest('uniqueKey', () => fetchDataFromAPI());
 */
export async function cachedInflightRequest(
  key: string,
  requestFn: () => Promise<any>
): Promise<any> {
  if (typeof key !== 'string' || key.trim() === '') {
    throw new Error('Key must be a non-empty string');
  }

  if (typeof requestFn !== 'function') {
    throw new Error('requestFn must be a function');
  }

  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key);
  }

  const promise = requestFn().finally(() => {
    inFlightRequests.delete(key);
  });

  inFlightRequests.set(key, promise);
  return promise;
}
